import type { jsPDF } from 'jspdf';
import type { Activity, DocumentItem, PartSelection, Ticket } from '../types';
import { api } from './api';
import { workPhase } from './workflow';

const BRAND = [191, 69, 18] as const;
const BRAND_DARK = [92, 32, 4] as const;
const BRAND_TINT = [253, 241, 236] as const;
const GRAY = [107, 114, 128] as const;
const TEXT = [31, 41, 55] as const;
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 14;
const FOOTER_TOP = 287;

type FontStyle = 'normal' | 'bold' | 'italic';

export interface ReportInput {
  ticket: Ticket;
  activities?: Activity[];
  images?: string[];
}

function formatDateTime(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('ro-RO', {
    timeZone: 'Europe/Bucharest',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatDuration(seconds?: number, minutes?: number): string {
  const totalSeconds = Math.max(0, Number(seconds) || Math.max(0, Number(minutes) || 0) * 60);
  if (totalSeconds <= 0) return '-';
  const roundedMinutes = Math.max(1, Math.ceil(totalSeconds / 60));
  const hours = Math.floor(roundedMinutes / 60);
  const remaining = roundedMinutes % 60;
  if (!hours) return `${roundedMinutes} ${roundedMinutes === 1 ? 'minut' : 'minute'}`;
  return remaining ? `${hours}h ${remaining}min` : `${hours}h`;
}

function ticketTypeLabel(type?: string): string {
  const normalized = String(type || '').trim().toUpperCase();
  return ({
    RECLAMATIE: 'Reclamație',
    PREVENTIE: 'Prevenție',
    MODIFICARI: 'Modificări teren',
    MODIFICARE: 'Modificare',
    INSTALARE: 'Instalare',
    INLOCUIRE: 'Înlocuire',
    RETRAGERE: 'Retragere',
    SERVICE: 'Service',
  } as Record<string, string>)[normalized] || String(type || 'Tichet');
}

function activityNames(ticket: Ticket, activities: Activity[]): string[] {
  const source = activities.length ? activities : ticket.interventionReport?.activities || ticket.intervention?.activities || ticket.activities || [];
  return source
    // Compatibilitate cu snapshoturile API V3 (`nume`) si rapoartele legacy.
    .map((activity) => {
      if (typeof activity === 'string') return activity;
      const row = activity as Activity & { nume?: string; label?: string; numeActivitate?: string };
      return row?.name || row?.nume || row?.label || row?.numeActivitate;
    })
    .map((name) => String(name || '').trim())
    .filter(Boolean);
}

function reportParts(ticket: Ticket): PartSelection[] {
  return (ticket.interventionReport?.parts || ticket.intervention?.partsUsed || ticket.partsUsed || []).filter((part) => Number(part.qty) > 0);
}

function reportDefectiveParts(ticket: Ticket): PartSelection[] {
  return (ticket.interventionReport?.defectiveParts || []).filter((part) => Number(part.qty) > 0);
}

async function toBase64(buffer: ArrayBuffer): Promise<string> {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 32_768;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return btoa(binary);
}

async function loadFonts(doc: jsPDF): Promise<boolean> {
  try {
    const load = async (url: string) => {
      const response = await fetch(url, { cache: 'force-cache' });
      if (!response.ok) throw new Error(`Font indisponibil: ${url}`);
      return toBase64(await response.arrayBuffer());
    };
    const [regular, bold, italic] = await Promise.all([
      load('/fonts/Roboto-Regular.ttf'),
      load('/fonts/Roboto-Bold.ttf'),
      load('/fonts/Roboto-Italic.ttf'),
    ]);
    doc.addFileToVFS('Roboto-Regular.ttf', regular);
    doc.addFileToVFS('Roboto-Bold.ttf', bold);
    doc.addFileToVFS('Roboto-Italic.ttf', italic);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
    doc.addFont('Roboto-Italic.ttf', 'Roboto', 'italic');
    return true;
  } catch (error) {
    console.warn('[PDF] Fonturile Roboto nu au putut fi încărcate.', error);
    return false;
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Imaginea nu poate fi citită.'));
    reader.readAsDataURL(blob);
  });
}

async function documentImages(documents: DocumentItem[]): Promise<string[]> {
  const imageDocuments = documents.filter((item) => String(item.mimeType || '').startsWith('image/')).slice(0, 9);
  const settled = await Promise.allSettled(imageDocuments.map(async (item) => {
    const url = item.previewUrl || item.downloadUrl || item.url;
    if (!url) throw new Error('Document fără adresă.');
    return blobToDataUrl(await api.documentBlob(url));
  }));
  return settled
    .filter((result): result is PromiseFulfilledResult<string> => result.status === 'fulfilled')
    .map((result) => result.value);
}

export async function buildInterventionReport({ ticket, activities = [], images = [] }: ReportInput): Promise<jsPDF> {
  const { jsPDF: Pdf } = await import('jspdf');
  const doc = new Pdf({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const hasRoboto = await loadFonts(doc);
  const setFont = (style: FontStyle = 'normal') => doc.setFont(hasRoboto ? 'Roboto' : 'helvetica', style);
  const phase = workPhase(ticket) === 'service' ? 'Service' : 'Teren';
  const activitiesList = activityNames(ticket, activities);
  const parts = reportParts(ticket);
  const defectiveParts = reportDefectiveParts(ticket);
  const observations = String(ticket.interventionReport?.observations || ticket.intervention?.observations || ticket.observations || '').trim();
  const clientFullName = [
    ticket.interventionReport?.clientLastName,
    ticket.interventionReport?.clientFirstName,
  ].map((value) => String(value || '').trim()).filter(Boolean).join(' ');
  const clientSignature = String(ticket.interventionReport?.clientSignature || '').trim();
  let y = 34;

  const addPage = () => {
    doc.addPage();
    y = 18;
    doc.setTextColor(...BRAND_DARK);
    doc.setFontSize(8);
    setFont('bold');
    doc.text(`Raport intervenție ${phase} - continuare`, MARGIN, 10);
  };
  const ensureSpace = (height: number) => {
    if (y + height > FOOTER_TOP - 8) addPage();
  };
  const sectionTitle = (title: string) => {
    ensureSpace(11);
    doc.setFontSize(10);
    setFont('bold');
    doc.setTextColor(...TEXT);
    doc.text(title, MARGIN, y);
    doc.setDrawColor(...BRAND);
    doc.setLineWidth(0.4);
    doc.line(MARGIN, y + 1.2, PAGE_WIDTH - MARGIN, y + 1.2);
    y += 6;
  };
  const keyValueRows = (rows: Array<[string, unknown]>) => {
    for (const [label, rawValue] of rows) {
      const value = String(rawValue || '-');
      const lines = doc.splitTextToSize(value, PAGE_WIDTH - (MARGIN * 2) - 44) as string[];
      const rowHeight = Math.max(7, lines.length * 4.6 + 2.4);
      ensureSpace(rowHeight);
      doc.setFontSize(9);
      setFont('bold');
      doc.setTextColor(...GRAY);
      doc.text(label, MARGIN + 3, y + 4.7);
      setFont('normal');
      doc.setTextColor(...TEXT);
      doc.text(lines, MARGIN + 41, y + 4.7);
      doc.setDrawColor(232, 232, 235);
      doc.setLineWidth(0.15);
      doc.line(MARGIN, y + rowHeight, PAGE_WIDTH - MARGIN, y + rowHeight);
      y += rowHeight;
    }
    y += 6;
  };
  const textBlock = (value: string, tinted = false, italic = false) => {
    const lines = doc.splitTextToSize(value || '-', PAGE_WIDTH - (MARGIN * 2) - 12) as string[];
    const blockHeight = Math.max(10, lines.length * 4.8 + 6);
    ensureSpace(blockHeight + 4);
    if (tinted) {
      doc.setFillColor(...BRAND_TINT);
      doc.roundedRect(MARGIN, y, PAGE_WIDTH - (MARGIN * 2), blockHeight, 2, 2, 'F');
    }
    doc.setFontSize(9);
    setFont(italic ? 'italic' : 'normal');
    const textColor = italic ? GRAY : TEXT;
    doc.setTextColor(textColor[0], textColor[1], textColor[2]);
    doc.text(lines, MARGIN + 6, y + 5.5);
    y += blockHeight + 6;
  };

  doc.setFillColor(...BRAND);
  doc.rect(0, 0, PAGE_WIDTH, 28, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  setFont('bold');
  doc.text('Nova Prompt SRL', MARGIN, 12);
  doc.setFontSize(9);
  setFont('normal');
  doc.text(`Raport Intervenție Tehnică - ${phase}`, MARGIN, 19);
  doc.setFontSize(8);
  doc.text(formatDateTime(ticket.completedAt || ticket.intervention?.endedAt || new Date().toISOString()), PAGE_WIDTH - MARGIN, 19, { align: 'right' });

  doc.setFillColor(...BRAND_TINT);
  doc.rect(MARGIN, y, PAGE_WIDTH - (MARGIN * 2), 10, 'F');
  doc.setTextColor(...BRAND_DARK);
  doc.setFontSize(10);
  setFont('bold');
  const displayId = String(ticket.ticketNumber ?? ticket.ticketId ?? ticket._id ?? '').toUpperCase();
  doc.text(`Tichet #${displayId}`, MARGIN + 3, y + 7);
  doc.setFontSize(8);
  setFont('normal');
  doc.text(ticketTypeLabel(ticket.type), PAGE_WIDTH - MARGIN - 3, y + 7, { align: 'right' });
  y += 14;

  sectionTitle('LOCAȚIE & DISPOZITIV');
  keyValueRows([
    ['Dispozitiv', ticket.deviceName],
    ['Cod dispozitiv', ticket.deviceCode],
    ['Locație', ticket.locationName],
    ['Adresă', ticket.locationAddress],
    ['Client', ticket.clientName],
  ]);

  sectionTitle('TIMPII INTERVENȚIEI');
  keyValueRows([
    ['Creat la', formatDateTime(ticket.createdAt)],
    ['Început la', formatDateTime(ticket.intervention?.startedAt)],
    ['Finalizat la', formatDateTime(ticket.completedAt || ticket.intervention?.endedAt)],
    ['Durată totală', formatDuration(ticket.intervention?.durationSeconds ?? ticket.durationSeconds, ticket.durationMinutes)],
  ]);

  sectionTitle('DESCRIERE PROBLEMĂ');
  textBlock(String(ticket.description || '-'));

  if (activitiesList.length) {
    sectionTitle('ACTIVITĂȚI EFECTUATE');
    for (const [index, activity] of activitiesList.entries()) {
      const lines = doc.splitTextToSize(activity, PAGE_WIDTH - (MARGIN * 2) - 18) as string[];
      const rowHeight = Math.max(7, lines.length * 4.6 + 2.4);
      ensureSpace(rowHeight);
      doc.setFontSize(9);
      setFont('bold');
      doc.setTextColor(...BRAND);
      doc.text(`${index + 1}.`, MARGIN + 3, y + 4.7);
      setFont('normal');
      doc.setTextColor(...TEXT);
      doc.text(lines, MARGIN + 13, y + 4.7);
      y += rowHeight;
    }
    y += 6;
  }

  if (parts.length) {
    sectionTitle('PIESE ÎNLOCUITE');
    ensureSpace(9);
    doc.setFillColor(...BRAND);
    doc.rect(MARGIN, y, PAGE_WIDTH - (MARGIN * 2), 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8.5);
    setFont('bold');
    doc.text('Cod', MARGIN + 3, y + 5.5);
    doc.text('Denumire piesă', MARGIN + 36, y + 5.5);
    doc.text('Cantitate', PAGE_WIDTH - MARGIN - 3, y + 5.5, { align: 'right' });
    y += 8;
    for (const [index, part] of parts.entries()) {
      const nameLines = doc.splitTextToSize(String(part.partName || '-'), 103) as string[];
      const rowHeight = Math.max(8, nameLines.length * 4.5 + 3);
      ensureSpace(rowHeight);
      if (index % 2 === 1) {
        doc.setFillColor(...BRAND_TINT);
        doc.rect(MARGIN, y, PAGE_WIDTH - (MARGIN * 2), rowHeight, 'F');
      }
      doc.setFontSize(8.5);
      setFont('bold');
      doc.setTextColor(...TEXT);
      doc.text(String(part.partCode || '-'), MARGIN + 3, y + 5.5);
      setFont('normal');
      doc.text(nameLines, MARGIN + 36, y + 5.5);
      setFont('bold');
      doc.setTextColor(...BRAND);
      doc.text(`x${Number(part.qty)}`, PAGE_WIDTH - MARGIN - 3, y + 5.5, { align: 'right' });
      y += rowHeight;
    }
    y += 6;
  }

  if (defectiveParts.length) {
    sectionTitle('PIESE DEFECTE DEMONTATE');
    ensureSpace(9);
    doc.setFillColor(...BRAND);
    doc.rect(MARGIN, y, PAGE_WIDTH - (MARGIN * 2), 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8.5);
    setFont('bold');
    doc.text('Cod', MARGIN + 3, y + 5.5);
    doc.text('Denumire piesă', MARGIN + 36, y + 5.5);
    doc.text('Cantitate', PAGE_WIDTH - MARGIN - 3, y + 5.5, { align: 'right' });
    y += 8;
    for (const [index, part] of defectiveParts.entries()) {
      const nameLines = doc.splitTextToSize(String(part.partName || '-'), 103) as string[];
      const rowHeight = Math.max(8, nameLines.length * 4.5 + 3);
      ensureSpace(rowHeight);
      if (index % 2 === 1) {
        doc.setFillColor(...BRAND_TINT);
        doc.rect(MARGIN, y, PAGE_WIDTH - (MARGIN * 2), rowHeight, 'F');
      }
      doc.setFontSize(8.5);
      setFont('normal');
      doc.setTextColor(...TEXT);
      doc.text(String(part.partCode || '-'), MARGIN + 3, y + 5.3);
      doc.text(nameLines, MARGIN + 36, y + 5.3);
      doc.text(String(part.qty), PAGE_WIDTH - MARGIN - 3, y + 5.3, { align: 'right' });
      y += rowHeight;
    }
    y += 6;
  }

  if (observations) {
    sectionTitle('OBSERVAȚII / RECOMANDĂRI');
    textBlock(observations, true, true);
  }

  if (images.length) {
    sectionTitle(`FOTOGRAFII (${images.length})`);
    const imageWidth = (PAGE_WIDTH - (MARGIN * 2) - 6) / 3;
    const imageHeight = imageWidth * 0.75;
    for (let index = 0; index < Math.min(images.length, 9); index += 1) {
      if (index % 3 === 0) ensureSpace(imageHeight + 5);
      const column = index % 3;
      const x = MARGIN + column * (imageWidth + 3);
      const image = images[index];
      try {
        if (!image) throw new Error('Imagine indisponibilă.');
        const format = image.startsWith('data:image/png') ? 'PNG' : 'JPEG';
        doc.addImage(image, format, x, y, imageWidth, imageHeight, undefined, 'FAST');
      } catch {
        doc.setFillColor(...BRAND_TINT);
        doc.rect(x, y, imageWidth, imageHeight, 'F');
        doc.setTextColor(...GRAY);
        doc.setFontSize(8);
        setFont('normal');
        doc.text('Fotografie indisponibilă', x + imageWidth / 2, y + imageHeight / 2, { align: 'center' });
      }
      if (column === 2 || index === Math.min(images.length, 9) - 1) y += imageHeight + 4;
    }
    y += 4;
  }

  ensureSpace(30);
  y = Math.max(y, PAGE_HEIGHT - 54);
  doc.setDrawColor(...GRAY);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y + 10, MARGIN + 55, y + 10);
  doc.line((PAGE_WIDTH / 2) + 5, y + 10, PAGE_WIDTH - MARGIN, y + 10);
  if (clientSignature) {
    try {
      doc.addImage(clientSignature, 'PNG', (PAGE_WIDTH / 2) + 8, y - 10, 48, 19, undefined, 'FAST');
    } catch (error) {
      console.warn('[PDF] Semnătura clientului nu a putut fi inclusă.', error);
    }
  }
  doc.setFontSize(8);
  setFont('normal');
  doc.setTextColor(...GRAY);
  doc.text('Semnătură tehnician', MARGIN, y + 15);
  doc.text('Semnătură client', (PAGE_WIDTH / 2) + 5, y + 15);
  doc.setTextColor(...TEXT);
  setFont('bold');
  doc.text(clientFullName || '-', (PAGE_WIDTH / 2) + 5, y + 21);

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFillColor(...BRAND);
    doc.rect(0, FOOTER_TOP, PAGE_WIDTH, PAGE_HEIGHT - FOOTER_TOP, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    setFont('normal');
    doc.text(`Nova Prompt SRL | Generat: ${formatDateTime(new Date().toISOString())} | Pagina ${page}/${pageCount}`, PAGE_WIDTH / 2, 293, { align: 'center' });
  }
  return doc;
}

export async function downloadInterventionReport(ticket: Ticket, activities: Activity[] = [], documents: DocumentItem[] = []) {
  const images = await documentImages(documents);
  const doc = await buildInterventionReport({ ticket, activities, images });
  const displayId = String(ticket.ticketNumber ?? ticket.ticketId ?? ticket._id ?? '').toUpperCase();
  doc.save(`Raport_${displayId}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
